// UserLayout.tsx

import React, { useEffect, useRef, useState } from "react";

import Footer from "../components/Footer";
import UserHeader from "./UserHeader";
import UserLoader from "./UserLoader";

import { putUserLogin } from "../handler/LoginAndSingupHandler";

import "../Dashboard/Design/Dashboard.css";
import "../Design/Responsive.css";

interface LayoutProps {
  children: React.ReactNode;
}

const UserLayout: React.FC<LayoutProps> = ({ children }) => {
  const hasRun = useRef(false);

  const [loader, setLoader] = useState(true);

  // ============================================================
  // LOGIN / REFRESH
  // ============================================================

  const userLoginOnRefresh = async () => {
    try {
      const response = await putUserLogin({
        growniverseId: "",
        password: "",
      });

      // ========================================================
      // SUCCESS
      // ========================================================

      if (response.statusCode === 200) {
        const userData = response.data.loginResponse;

        const pathName = window.location.pathname;

        // ======================================================
        // SAVE USER DATA
        // ======================================================

        localStorage.setItem(
          "appkey",
          userData.appKey
        );

        localStorage.setItem(
          "userProfile",
          JSON.stringify(userData.profile)
        );

        localStorage.setItem(
          "account",
          JSON.stringify(userData.account)
        );

        if (userData.profile.packageId === 0) {
          localStorage.setItem(
            "packages",
            JSON.stringify(userData.packages)
          );
        }

        // ======================================================
        // PACKAGE / HOLD / PROFILE REDIRECTION
        // ======================================================

        if (
          pathName !== "/package" &&
          pathName !== "/hold" &&
          pathName !== "/packageDetail" &&
          pathName !== "/payment"
        ) {
          // ----------------------------------------------------
          // PACKAGE REQUIRED
          // ----------------------------------------------------

          if (userData.profile.status === 2) {
            window.location.href = "/package";
            return;
          }

          // ----------------------------------------------------
          // ACCOUNT ON HOLD
          // ----------------------------------------------------

          if (userData.profile.status === 3) {
            window.location.href = "/hold";
            return;
          }

          // ----------------------------------------------------
          // AFFILIATE NOT APPROVED
          // ----------------------------------------------------

          if (
            !userData.profile.affiliateApproved &&
            pathName !== "/mycourses" &&
            pathName !== "/profile" &&
            pathName !== "/mywallet" &&
            pathName !== "/event" &&
            pathName !== "/support"
          ) {
            window.location.href = "/profile";
            return;
          }
        }

        // ======================================================
        // PAYMENT PAGE
        // ======================================================

        else if (pathName === "/payment") {
          if (
            userData.profile.status >= 4 &&
            userData.profile.affiliateApproved
          ) {
            window.location.href = "/dashboard";
            return;
          }

          if (!userData.profile.affiliateApproved) {
            window.location.href = "/mycourses";
            return;
          }
        }

        // ======================================================
        // HOLD PAGE
        // ======================================================

        else if (pathName === "/hold") {
          if (userData.profile.status < 3) {
            window.location.href = "/package";
            return;
          }
        }

        // ======================================================
        // ADMIN
        // ======================================================

        if (userData.profile.isAdmin) {
          window.location.href = "/packagepayment";
          return;
        }

        // ======================================================
        // STOP LOADER
        // ======================================================

        setLoader(false);
      }

      // ========================================================
      // SESSION INVALID
      // ========================================================

      else if (response.statusCode === 409) {
        localStorage.clear();

        window.location.href = "/";
      }

      // ========================================================
      // OTHER ERROR
      // ========================================================

      else {
        setLoader(false);
      }
    } catch (error) {
      console.error(
        "User refresh login failed:",
        error
      );

      setLoader(false);
    }
  };

  // ============================================================
  // INITIAL LOAD
  // ============================================================

  useEffect(() => {
    if (hasRun.current) return;

    const appKey =
      localStorage.getItem("appkey");

    // ----------------------------------------------------------
    // NO LOGIN
    // ----------------------------------------------------------

    if (!appKey) {
      window.location.href = "/";
      return;
    }

    // ----------------------------------------------------------
    // REFRESH LOGIN
    // ----------------------------------------------------------

    userLoginOnRefresh();

    hasRun.current = true;
  }, []);

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="growniverse-user-layout">

      {/* ======================================================
          LOADER
      ====================================================== */}

      {loader && <UserLoader />}

      {/* ======================================================
          MAIN APPLICATION
      ====================================================== */}

      {!loader && (
        <>
          {/* ====================================================
              FUTURISTIC FIXED HEADER
          ==================================================== */}

          <div
            className="growniverse-fixed-header"
          >
            <UserHeader />
          </div>

          {/* ====================================================
              MAIN CONTENT
          ==================================================== */}

          <div
            className="growniverse-layout-content"
          >
            <main>
              {children}
            </main>

            {/* ==================================================
                FOOTER
            ================================================== */}

            <Footer />
          </div>
        </>
      )}

      {/* ========================================================
          LAYOUT CSS
      ======================================================== */}

      <style>
        {`

/* ============================================================
   ROOT USER LAYOUT
============================================================ */

.growniverse-user-layout {
  position: relative;

  width: 100%;

  min-height: 100vh;

  margin: 0;
  padding: 0;

  background:
    radial-gradient(
      circle at 15% 10%,
      rgba(0, 246, 255, 0.035),
      transparent 28%
    ),
    radial-gradient(
      circle at 85% 15%,
      rgba(139, 92, 255, 0.035),
      transparent 30%
    ),
    linear-gradient(
      180deg,
      #05070d 0%,
      #070b14 45%,
      #05070d 100%
    );

  color: #ffffff;

  overflow-x: hidden;
}


/* ============================================================
   FIXED HEADER
============================================================ */

.growniverse-fixed-header {
  position: fixed;

  top: 0;
  left: 0;
  right: 0;

  width: 100%;

  z-index: 1200;

  margin: 0;
  padding: 0;

  background:
    rgba(5, 8, 16, 0.96);

  backdrop-filter:
    blur(18px);

  -webkit-backdrop-filter:
    blur(18px);

  box-shadow:
    0 8px 35px
    rgba(0, 0, 0, 0.45);
}


/* ============================================================
   MAIN CONTENT
============================================================ */

.growniverse-layout-content {
  position: relative;

  width: 100%;

  min-height: 100vh;

  margin: 0;

  /*
    UserHeader is fixed.
    Keep content below the header.
  */

  padding-top: 88px;

  background:
    transparent;

  overflow-x: hidden;
}


/* ============================================================
   MAIN
============================================================ */

.growniverse-layout-content main {
  position: relative;

  width: 100%;

  min-height: calc(
    100vh - 88px
  );

  margin: 0;

  padding: 0;

  background:
    transparent;
}


/* ============================================================
   REMOVE DEFAULT SPACING
============================================================ */

.growniverse-user-layout,
.growniverse-user-layout *,
.growniverse-layout-content,
.growniverse-layout-content main {
  box-sizing: border-box;
}


/* ============================================================
   BODY BACKGROUND
============================================================ */

:global(body) {
  margin: 0;

  padding: 0;

  background:
    #05070d;
}


/* ============================================================
   TABLET
============================================================ */

@media (max-width: 991px) {

  .growniverse-layout-content {
    padding-top: 82px;
  }

  .growniverse-layout-content main {
    min-height:
      calc(100vh - 82px);
  }

}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 767px) {

  .growniverse-fixed-header {
    width: 100vw;

    max-width: 100vw;

    left: 0;
    right: 0;
  }


  .growniverse-layout-content {
    width: 100%;

    padding-top: 70px;

    margin: 0;
  }


  .growniverse-layout-content main {
    width: 100%;

    min-height:
      calc(100vh - 70px);

    margin: 0;

    padding: 0;
  }

}


/* ============================================================
   VERY SMALL MOBILE
============================================================ */

@media (max-width: 400px) {

  .growniverse-layout-content {
    padding-top: 68px;
  }

}


/* ============================================================
   NO HORIZONTAL SCROLL
============================================================ */

html,
body {
  max-width: 100%;

  overflow-x: hidden;
}


/* ============================================================
   FUTURISTIC SELECTION
============================================================ */

::selection {
  background:
    rgba(0, 246, 255, 0.25);

  color:
    #ffffff;
}

`}
      </style>
    </div>
  );
};

export default UserLayout;